// src/components/common/PetDetailsBadge.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useColor } from "../../hooks/color/useColor";
import {
  GiSittingDog,
  GiCat,
  GiCow,
  GiGoat,
  GiRabbit,
  GiSquirrel,
  GiTortoise,
  GiHummingbird,
} from "react-icons/gi";
import { FaPen } from "react-icons/fa";
import PetDetailsDrawer from "../../pages/Orders/PetDetailsDrawer";
import AddNewPet from "../../pages/Orders/AddNewPet";

// Pet icon mapping
export const getPetIcon = (petType, className = "w-4 h-4") => {
  switch ((petType || "").toLowerCase()) {
    case "dog":
      return <GiSittingDog className={`${className} text-amber-600`} />;
    case "cat":
      return <GiCat className={`${className} text-gray-600`} />;
    case "cow":
      return <GiCow className={`${className} text-gray-700`} />;
    case "goat":
      return <GiGoat className={`${className} text-gray-700`} />;
    case "rabbit":
      return <GiRabbit className={`${className} text-pink-500`} />;
    case "squirrel":
      return <GiSquirrel className={`${className} text-orange-600`} />;
    case "tortoise":
    case "turtle":
      return <GiTortoise className={`${className} text-green-600`} />;
    case "bird":
    case "parrot":
      return <GiHummingbird className={`${className} text-blue-500`} />;
    default:
      return <GiSittingDog className={`${className} text-gray-500`} />;
  }
};

const PetDetailsBadge = ({
  // Pet Data
  petId,
  petName,
  petType,
  petBreed,
  petSize,
  petWeight,
  petAge,
  petGender,
  petTags = [],
  petMedicalConditions = [],
  petVaccinations = [],
  petColor,
  petMicrochipId,
  petRegistrationNumber,
  petNeuteredSpayed,
  petDateOfBirth,
  petLastVetVisit,
  petMedicalNotes,
  petBehaviorNotes,
  petImage,
  petVendorClientId,
  petVendorId,

  // Client Data for drawer
  clientData = {},
  clientAppointments = [],
  clientBoarding = [],
  triggerGetClientApi = false,

  // Display options
  showIcon = true,
  showBreed = true,
  showSize = true,
  showWeight = true,
  showAge = true,
  showGender = true,
  showTags = true,
  showSeparator = true,
  iconSize = "w-4 h-4",
  textSize = "text-sm",
  fontWeight = "font-semibold",
  className = "",
  separator = "|",
  tagClassName = "text-xs bg-gray-100 px-2 py-0.5 rounded-full",

  // Badge styling
  badgeClassName = "inline-flex items-center gap-1.5 transition-colors",
  nameColor = "text-blue-600",
  nameUnderlineColor = "border-blue-600",

  // Drawer props (view-details drawer)
  enableDrawer = true,
  drawerProps = {},
  onDrawerOpen,
  onDrawerClose,
  onRefresh,
  onClick,

  // Edit button + edit drawer props
  showEditButton = false,
  editIconClassName = "w-3.5 h-3.5",
  existingPets = [],
  onEditClick,
  onEditSave,
  onEditDrawerClose,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const { getColor } = useColor();

  // Create pet data object for drawer
  const petDataForDrawer = {
    _id: petId,
    name: petName,
    pet_type: { name: petType },
    breed: { breed_name: petBreed },
    size_details: { size_label: petSize },
    weight_kg: petWeight,
    weight: petWeight,
    age: petAge,
    gender: petGender,
    tags: petTags,
    medical_condition: petMedicalConditions,
    vaccinations: petVaccinations,
    color: petColor,
    microchip_id: petMicrochipId,
    registration_number: petRegistrationNumber,
    neutered_spayed: petNeuteredSpayed,
    date_of_birth: petDateOfBirth,
    last_vet_visit: petLastVetVisit,
    medical_notes: petMedicalNotes,
    behavior_notes: petBehaviorNotes,
    image: petImage,
    vendor_client_id: petVendorClientId,
    vendor_id: petVendorId,
  };

  const handleBadgeClick = (e) => {
    e.stopPropagation();

    if (!enableDrawer) {
      if (onClick) {
        onClick(petDataForDrawer, e);
      }
      return;
    }

    if (onDrawerOpen) {
      onDrawerOpen(petDataForDrawer);
    }
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    if (onDrawerClose) {
      onDrawerClose();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  // Edit button handlers
  const handleEditButtonClick = (e) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(petDataForDrawer);
    }
    setIsEditDrawerOpen(true);
  };

  const handleEditDrawerClose = () => {
    setIsEditDrawerOpen(false);
    if (onEditDrawerClose) {
      onEditDrawerClose();
    }
  };

  const handleEditDrawerSave = (updatedPet) => {
    if (onEditSave) {
      onEditSave(updatedPet);
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <>
      {/* Main Badge */}
      <div className={`flex flex-col ${className}`}>
        {/* First row: Icon + Name + Edit + Tags */}
        <div className={`${badgeClassName}`}>
          {/* Icon - not clickable */}
          {showIcon && petType && (
            <span className="flex-shrink-0">
              {getPetIcon(petType, iconSize)}
            </span>
          )}

          {/* Pet Name - ONLY this is clickable with underline on hover */}
          <span
            onClick={handleBadgeClick}
            className={`${textSize} ${fontWeight} ${nameColor} hover:underline hover:${nameUnderlineColor} cursor-pointer transition-all inline-block`}
            data-no-tooltip="true"
            title={enableDrawer ? `Click to view ${petName || 'pet'} details` : (onClick ? `Click ${petName || 'pet'}` : '')}
          >
            {petName}
          </span>

          {/* Edit Button - separate, independent hover, no underline */}
          {showEditButton && (
            <button
              type="button"
              onClick={handleEditButtonClick}
              className="flex-shrink-0 inline-flex items-center justify-center p-1 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title={`Edit ${petName || 'pet'}`}
              data-no-tooltip="true"
            >
              <FaPen className={editIconClassName} />
            </button>
          )}

          {/* Tags - NOT clickable, no underline */}
          {showTags && petTags && petTags.length > 0 && (
            <span className="flex items-center gap-1 flex-wrap ml-1">
              {Array.isArray(petTags) && petTags.map((tag, index) => {
                const tagText = typeof tag === "string" ? tag : tag.name || tag.label || "";
                return (
                  <span
                    key={`tag-${index}`}
                    className={`${tagClassName} ${getColor("petTag", tagText)}`}
                    style={{ pointerEvents: 'none' }}
                  >
                    {tagText}
                  </span>
                );
              })}
            </span>
          )}
        </div>

        {/* Details row - NOT clickable, no underline */}
        <div className="text-sm text-gray-500 flex items-center gap-1 flex-wrap mt-0.5">
          {showGender && petGender && (
            <>
              <span>
                {petGender?.toLowerCase() === "male" ? "♂" : "♀"} {petGender}
              </span>
              {showSeparator && <span className="text-gray-500">{separator}</span>}
            </>
          )}

          {showBreed && petBreed && (
            <>
              <span className="capitalize">{petBreed}</span>
              {showSeparator && <span className="text-gray-500">{separator}</span>}
            </>
          )}

          {showSize && petSize && (
            <>
              <span className="capitalize">{petSize}</span>
              {showSeparator && <span className="text-gray-500">{separator}</span>}
            </>
          )}

          {showWeight && petWeight && (
            <>
              <span>{petWeight} kg</span>
              {showSeparator && <span className="text-gray-500">{separator}</span>}
            </>
          )}

          {showAge && petAge && <span>{petAge}</span>}
        </div>
      </div>

      {/* Pet Details Drawer (view) - Only render if enabled */}
      {enableDrawer && isDrawerOpen && (
        <PetDetailsDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          petData={petDataForDrawer}
          clientData={clientData}
          clientAppointments={clientAppointments}
          clientBoarding={clientBoarding}
          triggergetclientapi={triggerGetClientApi}
          onRefresh={onRefresh}
          {...drawerProps}
        />
      )}

      {/* Edit Pet Drawer - Only render if enabled and open */}
      {showEditButton && isEditDrawerOpen && (
        <AddNewPet
          isOpen={isEditDrawerOpen}
          onClose={handleEditDrawerClose}
          onSave={handleEditDrawerSave}
          onEditSave={(originalData, updatedData) => {
            if (onEditSave) onEditSave(updatedData, originalData);
          }}
          clientId={petVendorClientId}
          vendorId={petVendorId}
          petId={petId}
          existingPets={existingPets}
          shouldHideSaveAndAdd={true}
        />
      )}
    </>
  );
};

// PropTypes remain the same
PetDetailsBadge.propTypes = {
  // Pet Data
  petId: PropTypes.string,
  petName: PropTypes.string,
  petType: PropTypes.string,
  petBreed: PropTypes.string,
  petSize: PropTypes.string,
  petWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  petAge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  petGender: PropTypes.string,
  petTags: PropTypes.array,
  petMedicalConditions: PropTypes.array,
  petVaccinations: PropTypes.array,
  petColor: PropTypes.string,
  petMicrochipId: PropTypes.string,
  petRegistrationNumber: PropTypes.string,
  petNeuteredSpayed: PropTypes.bool,
  petDateOfBirth: PropTypes.string,
  petLastVetVisit: PropTypes.string,
  petMedicalNotes: PropTypes.string,
  petBehaviorNotes: PropTypes.string,
  petImage: PropTypes.string,
  petVendorClientId: PropTypes.string,
  petVendorId: PropTypes.string,

  // Client Data
  clientData: PropTypes.object,
  clientAppointments: PropTypes.array,
  clientBoarding: PropTypes.array,
  triggerGetClientApi: PropTypes.bool,

  // Display options
  showIcon: PropTypes.bool,
  showBreed: PropTypes.bool,
  showSize: PropTypes.bool,
  showWeight: PropTypes.bool,
  showAge: PropTypes.bool,
  showGender: PropTypes.bool,
  showTags: PropTypes.bool,
  showSeparator: PropTypes.bool,
  iconSize: PropTypes.string,
  textSize: PropTypes.string,
  fontWeight: PropTypes.string,
  className: PropTypes.string,
  separator: PropTypes.string,
  tagClassName: PropTypes.string,

  // Badge styling
  badgeClassName: PropTypes.string,
  nameColor: PropTypes.string,
  nameUnderlineColor: PropTypes.string,

  // Drawer props
  enableDrawer: PropTypes.bool,
  drawerProps: PropTypes.object,
  onDrawerOpen: PropTypes.func,
  onDrawerClose: PropTypes.func,
  onRefresh: PropTypes.func,
  onClick: PropTypes.func,

  // Edit button + edit drawer
  showEditButton: PropTypes.bool,
  editIconClassName: PropTypes.string,
  existingPets: PropTypes.array,
  onEditClick: PropTypes.func,
  onEditSave: PropTypes.func,
  onEditDrawerClose: PropTypes.func,
};

export default PetDetailsBadge;